package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.ControlHeight
import co.maytes.standupbot.dashboard.styles.Elevation
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.dashboard.styles.elevation
import co.maytes.standupbot.shared.design.ElevationTokens
import co.maytes.standupbot.shared.design.ThemeScheme
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div

private fun px(value: Number): String = "${value.toInt()}px"

@Composable
fun SpaceSection(compact: Boolean) {
    GallerySection(
        id = "space",
        title = "Space and control height",
        description = "A 4px grid. s0_5 (2px) is for borders and optical nudges only. Controls come in three fixed heights.",
    ) {
        GalleryPanel {
            Space.entries.forEach { (name, size) ->
                Div(
                    attrs = {
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "72px 56px 1fr")
                            property("align-items", "center")
                            property("gap", "12px")
                        }
                    },
                ) {
                    GalleryMono(name)
                    GalleryMono(px(size.value))
                    Div(
                        attrs = {
                            style {
                                property("width", px(size.value))
                                property("max-width", "100%")
                                property("height", "12px")
                                property("background-color", Palette.accent.cssRgb())
                                property("border-radius", "2px")
                            }
                        },
                    )
                }
            }
        }
        GalleryHeading("Control heights")
        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "16px")
                    property("align-items", "flex-end")
                }
            },
        ) {
            ControlHeight.entries.forEach { (name, height) ->
                Stack(6) {
                    Div(
                        attrs = {
                            style {
                                property("height", px(height.value))
                                property("min-width", "112px")
                                property("background-color", Palette.surface.cssRgb())
                                property("border", "1px solid ${Palette.border.cssRgb()}")
                                property("border-radius", "${Radius.control}")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                        },
                    ) {
                        GalleryMono(px(height.value))
                    }
                    GalleryMono(name)
                }
            }
        }
        GalleryHeading("Row density")
        GalleryNote("Rows are 44px comfortable and 36px compact. Toggle the header switch to compare.")
        GalleryPanel {
            listOf("Ana Ruiz", "Ben Okafor", "Chloe Park").forEach { name ->
                Div(
                    attrs = {
                        style {
                            property("height", if (compact) "36px" else "44px")
                            property("display", "flex")
                            property("align-items", "center")
                            property("padding", "0 12px")
                            property("border-radius", "8px")
                            property("background-color", Palette.surfaceSubtle.cssRgb())
                            property("transition", Motion.transition(Motion.DUR_FAST, "height"))
                        }
                    },
                ) {
                    GalleryMono(name, Palette.textPrimary)
                }
            }
        }
    }
}

@Composable
fun RadiusSection() {
    GallerySection(
        id = "radius",
        title = "Radius",
        description = "Five roles. The pill has one spelling.",
    ) {
        GalleryGrid(minColumn = 132) {
            Radius.roles.forEach { (name, radius) ->
                Stack(8) {
                    Div(
                        attrs = {
                            style {
                                property("height", "72px")
                                property("background-color", Palette.accentSubtle.cssRgb())
                                property("border", "1px solid ${Palette.accentBorder.cssRgb()}")
                                property("border-radius", "$radius")
                            }
                        },
                    )
                    GalleryHeading(name)
                    GalleryMono(if (name == "pill") "9999px" else px(radius.value))
                }
            }
        }
    }
}

@Composable
fun ElevationSection(scheme: ThemeScheme) {
    GallerySection(
        id = "elevation",
        title = "Elevation",
        description = "Light uses the shadow ramp. Dark swaps drop shadows for a 1px border ring and a lighter surface step. " +
            "Showing the ${scheme.name.lowercase()} treatment.",
    ) {
        Div(
            attrs = {
                style {
                    property("background-color", Palette.background.cssRgb())
                    property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                    property("border-radius", "12px")
                    property("padding", "24px")
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fill, minmax(150px, 1fr))")
                    property("gap", "24px")
                }
            },
        ) {
            (0..Elevation.MAX_LEVEL).forEach { level ->
                val spec = ElevationTokens.spec(level, scheme)
                Stack(10) {
                    Div(
                        attrs = Modifier
                            .elevation(level)
                            .styleModifier {
                                property("height", "88px")
                                property("border-radius", "${Radius.surface}")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                            .toAttrs(),
                    ) {
                        SpanText(
                            text = "elevation($level)",
                            modifier = Modifier.styleModifier {
                                property("font-family", Typography.FONT_MONO)
                                property("font-size", "${Typography.caption.size}")
                                property("color", Palette.textSecondary.cssRgb())
                            },
                        )
                    }
                    GalleryMono("--sb-elev-$level")
                    GalleryMono(spec.surface, Palette.textTertiary)
                }
            }
        }
    }
}

@Composable
fun ZIndexSection() {
    GallerySection(
        id = "z-index",
        title = "Z-index",
        description = "No raw numbers in screens. The overlay components use these tokens.",
    ) {
        GalleryPanel {
            ZIndex.entries.forEach { (name, value) ->
                Div(
                    attrs = {
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "96px 64px 1fr")
                            property("align-items", "center")
                            property("gap", "12px")
                        }
                    },
                ) {
                    GalleryHeading(name)
                    GalleryMono("$value")
                    Div(
                        attrs = {
                            style {
                                property("height", "8px")
                                property("width", "${(value * 100 / ZIndex.tooltip).coerceAtLeast(1)}%")
                                property("background-color", Palette.accent.cssRgb())
                                property("border-radius", "4px")
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
fun LayoutSection() {
    GallerySection(
        id = "layout",
        title = "Layout",
        description = "Two content measures plus the shell, three gutters and four breakpoints.",
    ) {
        GalleryPanel {
            Layout.widths.forEach { (name, width) ->
                Stack(6) {
                    GalleryMono("$name · ${px(width.value)}")
                    Div(
                        attrs = {
                            style {
                                property("width", px(width.value))
                                property("max-width", "100%")
                                property("height", "24px")
                                property("background-color", Palette.accentSubtle.cssRgb())
                                property("border", "1px solid ${Palette.accentBorder.cssRgb()}")
                                property("border-radius", "6px")
                            }
                        },
                    )
                }
            }
        }
        GalleryGrid(minColumn = 200) {
            GalleryPanel {
                GalleryHeading("Gutters")
                GalleryMono("mobile · ${px(Layout.gutterMobile.value)}")
                GalleryMono("tablet · ${px(Layout.gutterTablet.value)}")
                GalleryMono("desktop · ${px(Layout.gutterDesktop.value)}")
            }
            GalleryPanel {
                GalleryHeading("Breakpoints")
                Layout.breakpoints.forEach { (name, width) -> GalleryMono("$name · ${width}px") }
            }
        }
    }
}
